import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  IconButton,
  Divider,
  Checkbox,
  ListItemText
} from '@material-ui/core';
import {
  CloudUpload,
  Delete,
  Description,
  Stars,
  Cancel,
  Save,
  Edit,
  Add
} from '@material-ui/icons';
import swal from 'sweetalert';
import firebase from 'firebase/app';
import 'firebase/database';
import TopMenu from '../TopMenu';
import PdfTextExtractionService from '../../services/PdfTextExtractionService';
import { makeApiCall } from '../../services/ApiUtils';
import './AITestGenerator.css';

const AITestGenerator = () => {
  const history = useHistory();
  const [generating, setGenerating] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [jdText, setJdText] = useState('');
  const [extractedRole, setExtractedRole] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    roleName: '',
    competenciesAndSkills: '',
    techStack: '',
    level: '',
    questionTypes: [],
    duration: '',
    numberOfQuestions: '',
    aiInstructions: ''
  });

  const [generatedQuestions, setGeneratedQuestions] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editableQuestions, setEditableQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testTitle, setTestTitle] = useState('');
  const [testCode, setTestCode] = useState('');

  const questionTypeOptions = [
    { value: 'MCQ', label: 'Multiple Choice Questions' },
    { value: 'Descriptive', label: 'Descriptive' },
    { value: 'Situational', label: 'Situational' },
    { value: 'Logical', label: 'Logical Reasoning' },
    { value: 'Coding', label: 'Coding' },
    { value: 'Video', label: 'Video Assignment' },
    { value: 'Psychometric', label: 'Psychometric Test' }
  ];

  const levelOptions = [
    { value: 'Fresher', label: 'Fresher' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Difficult', label: 'Difficult' }
  ];

  // Handle PDF file selection
  const handlePdfSelect = useCallback(async (file) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      swal('Error', 'Please select a PDF file.', 'error');
      return;
    }

    setPdfFile(file);
    setUploadingPdf(true);
    setJdText('');
    setExtractedRole('');

    try {
      // Extract text from PDF
      const extractionResult = await PdfTextExtractionService.extractTextFromFile(file);
      
      if (extractionResult.text && extractionResult.text.trim()) {
        setJdText(extractionResult.text);
        
        // Try to extract role name from JD
        try {
          const roleExtractionResult = await makeApiCall('/api/extract-role-from-jd', {
            jdText: extractionResult.text
          });
          
          if (roleExtractionResult && roleExtractionResult.role) {
            setExtractedRole(roleExtractionResult.role);
            setFormData(prev => ({ ...prev, roleName: roleExtractionResult.role }));
          }
        } catch (error) {
          console.log('Role extraction failed, user can enter manually:', error);
        }
      } else {
        swal('Error', 'Failed to extract text from PDF. Please try again or enter JD manually.', 'error');
      }
    } catch (error) {
      console.error('PDF extraction error:', error);
      swal('Error', 'Failed to process PDF file. Please try again.', 'error');
    } finally {
      setUploadingPdf(false);
    }
  }, []);

  // Handle file input change
  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handlePdfSelect(file);
    }
    if (e.target) {
      e.target.value = '';
    }
  }, [handlePdfSelect]);

  // Remove PDF
  const removePdf = useCallback(() => {
    setPdfFile(null);
    setJdText('');
    setExtractedRole('');
    setFormData(prev => ({ ...prev, roleName: '' }));
  }, []);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle question type selection
  const handleQuestionTypeChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      questionTypes: typeof value === 'string' ? value.split(',') : value
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!jdText.trim() && !pdfFile) {
      swal('Validation Error', 'Please upload a JD PDF or enter JD text manually.', 'error');
      return false;
    }

    if (!formData.roleName.trim()) {
      swal('Validation Error', 'Please enter or select a role name.', 'error');
      return false;
    }

    if (!formData.level) {
      swal('Validation Error', 'Please select a difficulty level.', 'error');
      return false;
    }

    if (formData.questionTypes.length === 0) {
      swal('Validation Error', 'Please select at least one question type.', 'error');
      return false;
    }

    if (!formData.duration || parseInt(formData.duration) <= 0) {
      swal('Validation Error', 'Please enter a valid test duration (in minutes).', 'error');
      return false;
    }

    if (!formData.numberOfQuestions || parseInt(formData.numberOfQuestions) <= 0) {
      swal('Validation Error', 'Please enter a valid number of questions.', 'error');
      return false;
    }

    return true;
  };

  // Generate questions
  const handleGenerateQuestions = async () => {
    if (!validateForm()) return;

    setGenerating(true);
    setGeneratedQuestions(null);

    try {
      // Split competencies and skills from merged field
      const competenciesAndSkills = formData.competenciesAndSkills || '';
      const requestData = {
        jdText: jdText || '',
        roleName: formData.roleName,
        competencies: competenciesAndSkills,
        skills: competenciesAndSkills,
        techStack: formData.techStack,
        level: formData.level,
        questionTypes: formData.questionTypes,
        duration: parseInt(formData.duration),
        numberOfQuestions: parseInt(formData.numberOfQuestions),
        aiInstructions: formData.aiInstructions || ''
      };

      const result = await makeApiCall('/api/generate-questions', requestData);

      if (result && (result.questions || (result.data && result.data.questions))) {
        const questions = result.questions || result.data.questions;
        setGeneratedQuestions({ questions });
        setEditableQuestions(questions.map(q => ({ ...q })));
        setPreviewDialogOpen(true);
      } else {
        throw new Error('Invalid response from API');
      }
    } catch (error) {
      console.error('Question generation error:', error);
      swal('Error', `Failed to generate questions: ${error.message}`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Handle question edit
  const handleQuestionEdit = (index, field, value) => {
    const updated = [...editableQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setEditableQuestions(updated);
  };

  // Handle option edit for MCQ
  const handleOptionEdit = (questionIndex, optionIndex, value) => {
    const updated = [...editableQuestions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options[optionIndex] = value;
    setEditableQuestions(updated);
  };

  // Save test to Firebase
  const handleSaveTest = async () => {
    if (!testTitle.trim()) {
      swal('Validation Error', 'Please enter a test title.', 'error');
      return;
    }

    if (!testCode.trim()) {
      swal('Validation Error', 'Please enter a test code.', 'error');
      return;
    }

    if (testCode.trim().length < 3) {
      swal('Validation Error', 'Test code must be at least 3 characters long.', 'error');
      return;
    }

    // Check if test code already exists
    try {
      const examRef = firebase.database().ref(`TestPapers/${testCode.trim()}`);
      const snapshot = await examRef.once('value');
      if (snapshot.exists()) {
        swal('Duplicate Test Code', 'This test code already exists. Please choose a different code.', 'error');
        return;
      }
    } catch (error) {
      console.error('Error checking test code:', error);
    }

    setSaving(true);

    try {
      // Convert questions to database format - matching DynamicExamCreator structure exactly
      const sections = [{
        id: 'section-1',
        t: 'Main Section',
        w: 100,
        q: editableQuestions.map((q, index) => {
          // Map question types to match DynamicExamCreator format
          let questionType = 'text'; // default
          const qTypeLower = (q.type || '').toLowerCase();
          
          if (qTypeLower === 'mcq') {
            questionType = 'mcq-single'; // Default to single choice for AI-generated MCQs
          } else if (qTypeLower === 'psychometric') {
            questionType = 'psyco';
          } else if (qTypeLower === 'coding') {
            questionType = 'code';
          } else if (qTypeLower === 'video') {
            questionType = 'video';
          } else if (qTypeLower === 'descriptive') {
            questionType = 'text';
          } else if (qTypeLower === 'situational') {
            questionType = 'scenario-text';
          } else if (qTypeLower === 'logical') {
            questionType = 'text'; // Logical questions are saved as text type
          } else {
            questionType = 'text';
          }

          const questionData = {
            id: `q-${index + 1}`,
            type: questionType,
            text: (q.question || q.text || '').trim(),
            ai: false,
            maxScore: q.maxScore !== undefined ? Number(q.maxScore) : 10
          };

          // Add AI instructions if present (using shortened field name 'inst')
          if (formData.aiInstructions && formData.aiInstructions.trim()) {
            questionData.inst = formData.aiInstructions.trim();
          }

          // Add options for MCQ questions (using shortened field names)
          if ((questionType === 'mcq-single' || questionType === 'mcq-multi') && q.options && q.options.length > 0) {
            questionData.opts = q.options.map((opt, optIndex) => {
              const optText = typeof opt === 'string' ? opt : (opt.text || opt || '');
              const optId = typeof opt === 'object' && opt.id ? opt.id : `opt-${index}-${optIndex}`;
              
              // Determine if this option is correct
              let isCorrect = false;
              if (q.correctAnswer) {
                // correctAnswer might be 'A', 'B', 'C', 'D' or option index
                const answerChar = q.correctAnswer.toUpperCase();
                if (answerChar.length === 1 && answerChar >= 'A' && answerChar <= 'Z') {
                  const answerIndex = answerChar.charCodeAt(0) - 65;
                  isCorrect = (optIndex === answerIndex);
                } else {
                  // Try to match by option text or ID
                  isCorrect = (optText === q.correctAnswer || optId === q.correctAnswer);
                }
              } else if (typeof opt === 'object' && opt.isCorrect !== undefined) {
                isCorrect = Boolean(opt.isCorrect);
              }
              
              return {
                id: optId,
                t: optText.trim(),
                m: (typeof opt === 'object' && opt.marks !== undefined) ? Number(opt.marks) : (isCorrect ? 1 : 0),
                c: isCorrect
              };
            });
            
            // Ensure at least one option is marked as correct for MCQ questions
            const hasCorrectAnswer = questionData.opts.some(opt => opt.c === true);
            if (!hasCorrectAnswer && questionData.opts.length > 0) {
              // If no correct answer found, mark the first option as correct (fallback)
              console.warn(`No correct answer found for MCQ question ${questionData.id}, marking first option as correct`);
              questionData.opts[0].c = true;
              questionData.opts[0].m = 1;
            }
          }

          // Add evaluation points for text and video questions (exactly as DynamicExamCreator does)
          if (questionType === 'text' || questionType === 'video') {
            if (q.evaluationPoints) {
              if (typeof q.evaluationPoints === 'string') {
                questionData.evaluationPoints = q.evaluationPoints.trim();
              } else if (Array.isArray(q.evaluationPoints) && q.evaluationPoints.length > 0) {
                questionData.evaluationPoints = q.evaluationPoints.join(', ');
              } else {
                questionData.evaluationPoints = '';
              }
            } else {
              // Always save evaluationPoints field (even if empty) for text and video questions
              questionData.evaluationPoints = '';
            }
          }

          // Add problem description for coding questions (using shortened field name 'pd')
          if (questionType === 'code' && q.problemDescription) {
            questionData.pd = q.problemDescription.trim();
          }

          // Add boilerplate code for coding questions (using shortened field name 'bc')
          if (questionType === 'code' && q.boilerplateCode) {
            questionData.bc = q.boilerplateCode.trim();
          }

          // Add evaluation keywords (using shortened field name 'ek')
          if (q.evaluationKeywords) {
            questionData.ek = q.evaluationKeywords.trim();
          }

          // Add language for coding questions (stored in instructions or as separate field)
          if (questionType === 'code' && q.language) {
            // Store language in instructions if not already set
            if (!questionData.inst) {
              questionData.inst = `Programming Language: ${q.language}`;
            } else {
              questionData.inst = `${questionData.inst}\nProgramming Language: ${q.language}`;
            }
          }

          // Add psychometric answer (for psyco type)
          if (questionType === 'psyco' && q.psycoAnswer) {
            questionData.psycoAnswer = q.psycoAnswer.trim().toUpperCase();
          }

          return questionData;
        })
      }];

      // Set start date to now and end date to 1 year from now (or based on duration)
      const now = Date.now();
      const oneYearFromNow = now + (365 * 24 * 60 * 60 * 1000); // 1 year in milliseconds
      
      const examData = {
        title: testTitle.trim(),
        code: testCode.trim(),
        timer: parseInt(formData.duration) || 60,
        start: now, // Start immediately
        end: oneYearFromNow, // End 1 year from now (admin can adjust later)
        sections: sections,
        created: firebase.database.ServerValue.TIMESTAMP,
        dynamic: true,
        by: sessionStorage.getItem("email") || 'Unknown'
      };

      await firebase.database().ref(`TestPapers/${examData.code}`).set(examData);
      
      swal('Success', 'Test saved successfully!', 'success').then(() => {
        setPreviewDialogOpen(false);
        // Reset form for new test
        setFormData({
          roleName: '',
          competenciesAndSkills: '',
          techStack: '',
          level: '',
          questionTypes: [],
          duration: '',
          numberOfQuestions: '',
          aiInstructions: ''
        });
        setGeneratedQuestions(null);
        setEditableQuestions([]);
        setTestTitle('');
        setTestCode('');
        setJdText('');
        setPdfFile(null);
        setExtractedRole('');
      });
    } catch (error) {
      console.error('Error saving test:', error);
      swal('Error', `Failed to save test: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="ai-test-generator">
      <TopMenu showHome showLogout />
      <Container maxWidth="lg" style={{ marginTop: 80, padding: 24 }}>
        <Paper elevation={3} style={{ padding: 32 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Stars style={{ marginRight: 12, fontSize: 32, color: '#2196f3' }} />
            <Typography variant="h4" component="h1">
              AI Test Generation Engine
            </Typography>
          </Box>

          <Typography variant="body1" color="textSecondary" paragraph>
            Generate comprehensive question papers based on Job Description, role requirements, and assessment criteria.
          </Typography>

          <Divider style={{ marginBottom: 32 }} />

          <Grid container spacing={3}>
            {/* JD Upload Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ padding: 16 }}>
                <Typography variant="h6" gutterBottom>
                  Job Description (JD)
                </Typography>
                <Typography variant="caption" color="textSecondary" paragraph>
                  Upload a PDF file containing the Job Description. The system will extract text and attempt to identify the role.
                </Typography>

                {!pdfFile ? (
                  <div>
                    <input
                      accept=".pdf,application/pdf"
                      style={{ display: 'none' }}
                      id="pdf-upload-input"
                      type="file"
                      onChange={handleFileInputChange}
                    />
                    <label htmlFor="pdf-upload-input">
                      <Button
                        variant="outlined"
                        color="primary"
                        component="span"
                        startIcon={<CloudUpload />}
                        disabled={uploadingPdf}
                        fullWidth
                      >
                        {uploadingPdf ? 'Processing PDF...' : 'Upload JD PDF'}
                      </Button>
                    </label>
                    {uploadingPdf && (
                      <Box mt={2}>
                        <LinearProgress />
                      </Box>
                    )}
                  </div>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                    <Box display="flex" alignItems="center">
                      <Description color="primary" style={{ marginRight: 8 }} />
                      <Typography variant="body2">{pdfFile.name}</Typography>
                    </Box>
                    <IconButton size="small" onClick={removePdf} color="secondary">
                      <Delete />
                    </IconButton>
                  </Box>
                )}

                {jdText && (
                  <Box mt={2}>
                    <TextField
                      label="Extracted JD Text"
                      multiline
                      rows={4}
                      fullWidth
                      value={jdText}
                      onChange={(e) => setJdText(e.target.value)}
                      variant="outlined"
                      helperText="You can edit the extracted text if needed"
                    />
                  </Box>
                )}

                {!pdfFile && (
                  <Box mt={2}>
                    <TextField
                      label="Or Enter JD Text Manually"
                      multiline
                      rows={6}
                      fullWidth
                      value={jdText}
                      onChange={(e) => setJdText(e.target.value)}
                      variant="outlined"
                      placeholder="Paste or type the Job Description here..."
                    />
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Role Name */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Role Name"
                fullWidth
                required
                value={formData.roleName}
                onChange={(e) => handleInputChange('roleName', e.target.value)}
                variant="outlined"
                helperText={extractedRole ? `Extracted from JD: ${extractedRole}` : 'Enter or select role name'}
                InputProps={{
                  endAdornment: extractedRole && formData.roleName !== extractedRole && (
                    <Chip
                      label="Use Extracted"
                      size="small"
                      onClick={() => handleInputChange('roleName', extractedRole)}
                      style={{ cursor: 'pointer' }}
                    />
                  )
                }}
              />
            </Grid>

            {/* Complexity Level */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required variant="outlined">
                <InputLabel>Complexity Level</InputLabel>
                <Select
                  value={formData.level}
                  onChange={(e) => handleInputChange('level', e.target.value)}
                  label="Complexity Level"
                >
                  {levelOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Competencies and Skills (Merged) */}
            <Grid item xs={12}>
              <TextField
                label="Competencies and Skills (comma-separated)"
                fullWidth
                value={formData.competenciesAndSkills}
                onChange={(e) => handleInputChange('competenciesAndSkills', e.target.value)}
                variant="outlined"
                placeholder="e.g., Problem Solving, Communication, JavaScript, React, Node.js"
                helperText="List key competencies and skills required for this role"
                multiline
                rows={3}
              />
            </Grid>

            {/* Tech Stack or Tools */}
            <Grid item xs={12}>
              <TextField
                label="Tech-stack or Tools (comma-separated)"
                fullWidth
                value={formData.techStack}
                onChange={(e) => handleInputChange('techStack', e.target.value)}
                variant="outlined"
                placeholder="e.g., React, Node.js, MongoDB, AWS"
                helperText="List technologies, frameworks, and tools required"
              />
            </Grid>

            {/* Question Types */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required variant="outlined">
                <InputLabel>Question Types</InputLabel>
                <Select
                  multiple
                  value={formData.questionTypes}
                  onChange={handleQuestionTypeChange}
                  label="Question Types"
                  renderValue={(selected) => (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((value) => {
                        const option = questionTypeOptions.find(opt => opt.value === value);
                        return <Chip key={value} label={option?.label || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {questionTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={formData.questionTypes.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select one or more question types (multiple select)</FormHelperText>
              </FormControl>
            </Grid>

            {/* AI Instructions */}
            <Grid item xs={12}>
              <TextField
                label="AI Instructions"
                fullWidth
                value={formData.aiInstructions}
                onChange={(e) => handleInputChange('aiInstructions', e.target.value)}
                variant="outlined"
                placeholder="e.g., Focus on practical scenarios, emphasize problem-solving approach, include real-world examples"
                helperText="Additional instructions for AI to consider while generating questions"
                multiline
                rows={3}
              />
            </Grid>

            {/* Test Format */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Duration (minutes)"
                type="number"
                fullWidth
                required
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
                variant="outlined"
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Number of Questions"
                type="number"
                fullWidth
                required
                value={formData.numberOfQuestions}
                onChange={(e) => handleInputChange('numberOfQuestions', e.target.value)}
                variant="outlined"
                inputProps={{ min: 1 }}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => history.push('/admin-dashboard')}
                  startIcon={<Cancel />}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateQuestions}
                  disabled={generating}
                  startIcon={generating ? <CircularProgress size={20} /> : <Stars />}
                >
                  {generating ? 'Generating Questions...' : 'Generate Questions'}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Preview Dialog */}
          <Dialog
            open={previewDialogOpen}
            onClose={() => setPreviewDialogOpen(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">Generated Question Paper</Typography>
                <Button onClick={() => setPreviewDialogOpen(false)}>
                  Close
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              {/* Save Test Form */}
              <Paper variant="outlined" style={{ padding: 16, marginBottom: 24, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>Save Test</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Test Title"
                      fullWidth
                      required
                      value={testTitle}
                      onChange={(e) => setTestTitle(e.target.value)}
                      variant="outlined"
                      placeholder="e.g., Senior Developer Assessment"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Test Code"
                      fullWidth
                      required
                      value={testCode}
                      onChange={(e) => setTestCode(e.target.value)}
                      variant="outlined"
                      placeholder="e.g., TEST001"
                      helperText="Unique code for this test (min 3 characters)"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {editableQuestions.length > 0 && (
                <Box>
                  {/* Header Information */}
                  <Paper variant="outlined" style={{ padding: 16, marginBottom: 24, backgroundColor: '#f5f5f5' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2"><strong>Role:</strong> {formData.roleName}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2"><strong>Complexity Level:</strong> {formData.level}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2"><strong>Total Questions:</strong> {editableQuestions.length}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2"><strong>Duration:</strong> {formData.duration} minutes</Typography>
                      </Grid>
                      {formData.competenciesAndSkills && (
                        <Grid item xs={12}>
                          <Typography variant="body2"><strong>Competencies and Skills:</strong> {formData.competenciesAndSkills}</Typography>
                        </Grid>
                      )}
                      {formData.techStack && (
                        <Grid item xs={12}>
                          <Typography variant="body2"><strong>Tech-stack or Tools:</strong> {formData.techStack}</Typography>
                        </Grid>
                      )}
                      {formData.aiInstructions && (
                        <Grid item xs={12}>
                          <Typography variant="body2"><strong>AI Instructions:</strong> {formData.aiInstructions}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  <Divider style={{ marginBottom: 24 }} />

                  {/* Questions List */}
                  {editableQuestions.map((q, index) => (
                    <Paper 
                      key={index} 
                      variant="outlined" 
                      style={{ 
                        padding: 20, 
                        marginBottom: 24,
                        backgroundColor: '#ffffff',
                        borderLeft: '4px solid #2196f3'
                      }}
                    >
                      {/* Question Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6" style={{ color: '#2196f3', fontWeight: 'bold' }}>
                            Q{index + 1}.
                          </Typography>
                          <Chip 
                            label={q.type} 
                            size="small" 
                            color="primary"
                            style={{ fontWeight: 'bold' }}
                          />
                          {q.maxScore && (
                            <Chip 
                              label={`${q.maxScore} marks`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => setEditingQuestionIndex(editingQuestionIndex === index ? null : index)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Box>

                      {/* Question Text - Editable */}
                      {editingQuestionIndex === index ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={q.question || q.text || ''}
                          onChange={(e) => handleQuestionEdit(index, 'question', e.target.value)}
                          variant="outlined"
                          label="Question Text"
                          style={{ marginBottom: 16 }}
                        />
                      ) : (
                      <Typography 
                        variant="body1" 
                        paragraph 
                        style={{ 
                          fontSize: '16px',
                          lineHeight: '1.6',
                          marginBottom: q.options && q.options.length > 0 ? 16 : 8
                        }}
                      >
                          {q.question || q.text || ''}
                      </Typography>
                      )}

                      {/* Options for MCQ - Editable */}
                      {q.options && q.options.length > 0 && (
                        <Box ml={2} mt={2}>
                          {q.options.map((opt, optIndex) => (
                            <Box 
                              key={optIndex} 
                              display="flex" 
                              alignItems="flex-start"
                              style={{ 
                                marginBottom: 8,
                                padding: '8px 12px',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '4px'
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                style={{ 
                                  fontWeight: 'bold',
                                  marginRight: 12,
                                  minWidth: 24,
                                  color: '#2196f3'
                                }}
                              >
                                {String.fromCharCode(65 + optIndex)}.
                              </Typography>
                              {editingQuestionIndex === index ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={typeof opt === 'string' ? opt : opt.text || opt}
                                  onChange={(e) => handleOptionEdit(index, optIndex, e.target.value)}
                                  variant="outlined"
                                  style={{ flex: 1 }}
                                />
                              ) : (
                              <Typography variant="body2" style={{ flex: 1 }}>
                                  {typeof opt === 'string' ? opt : opt.text || opt}
                              </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Correct Answer (for MCQ) - Editable */}
                      {(q.correctAnswer || editingQuestionIndex === index) && (
                        <Box mt={2}>
                          {editingQuestionIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Correct Answer (A, B, C, or D)"
                              value={q.correctAnswer || ''}
                              onChange={(e) => handleQuestionEdit(index, 'correctAnswer', e.target.value.toUpperCase())}
                              variant="outlined"
                              style={{ backgroundColor: '#e8f5e9' }}
                            />
                          ) : (
                            <Box 
                          p={1.5}
                          style={{ 
                            backgroundColor: '#e8f5e9',
                            borderRadius: '4px',
                            borderLeft: '3px solid #4caf50'
                          }}
                        >
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                            ✓ Correct Answer: {q.correctAnswer}
                          </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Evaluation Points (for Descriptive/Situational) - Editable */}
                      {(q.evaluationPoints || editingQuestionIndex === index) && (
                        <Box mt={2}>
                          {editingQuestionIndex === index ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Evaluation Criteria"
                              value={q.evaluationPoints || ''}
                              onChange={(e) => handleQuestionEdit(index, 'evaluationPoints', e.target.value)}
                              variant="outlined"
                              style={{ backgroundColor: '#fff3e0' }}
                            />
                          ) : q.evaluationPoints ? (
                            <Box 
                          p={1.5}
                          style={{ 
                            backgroundColor: '#fff3e0',
                            borderRadius: '4px',
                            borderLeft: '3px solid #ff9800'
                          }}
                        >
                          <Typography variant="body2" style={{ fontWeight: 'bold', color: '#e65100', marginBottom: 4 }}>
                            Evaluation Criteria:
                          </Typography>
                          <Typography variant="body2" style={{ color: '#5d4037' }}>
                            {q.evaluationPoints}
                          </Typography>
                            </Box>
                          ) : null}
                        </Box>
                      )}

                      {/* Max Score - Editable */}
                      {editingQuestionIndex === index && (
                        <Box mt={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Max Score"
                            value={q.maxScore || 10}
                            onChange={(e) => handleQuestionEdit(index, 'maxScore', parseInt(e.target.value) || 10)}
                            variant="outlined"
                            inputProps={{ min: 1 }}
                          />
                        </Box>
                      )}

                      {/* Language (for Coding) - Editable */}
                      {(q.language || (editingQuestionIndex === index && q.type === 'Coding')) && (
                        <Box mt={2}>
                          {editingQuestionIndex === index && q.type === 'Coding' ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Programming Language"
                              value={q.language || ''}
                              onChange={(e) => handleQuestionEdit(index, 'language', e.target.value)}
                              variant="outlined"
                              placeholder="e.g., javascript, python, java"
                            />
                          ) : q.language ? (
                          <Chip 
                            label={`Language: ${q.language}`} 
                            size="small" 
                            variant="outlined"
                            color="secondary"
                          />
                          ) : null}
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setPreviewDialogOpen(false)} 
                variant="outlined"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTest}
                variant="contained" 
                color="primary"
                disabled={saving || !testTitle.trim() || !testCode.trim()}
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              >
                {saving ? 'Saving...' : 'Save Test'}
              </Button>
              <Button 
                onClick={() => {
                  setPreviewDialogOpen(false);
                  setFormData({
                    roleName: '',
                    competenciesAndSkills: '',
                    techStack: '',
                    level: '',
                    questionTypes: [],
                    duration: '',
                    numberOfQuestions: '',
                    aiInstructions: ''
                  });
                  setGeneratedQuestions(null);
                  setEditableQuestions([]);
                  setTestTitle('');
                  setTestCode('');
                  setJdText('');
                  setPdfFile(null);
                  setExtractedRole('');
                }}
                variant="contained" 
                color="secondary"
                startIcon={<Add />}
              >
                Create New Test
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </div>
  );
};

export default AITestGenerator;

